import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { xadmApi } from '../../api/xadm.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { LineChart } from '../../components/LineChart.js';
import { useUrlUpdate } from '../../lib/hooks.js';
import { num } from '../../lib/format.js';

/**
 * 방문 — 원본 /home/bananapartner/xadm/visit_list.php.
 *
 * 요일(일~토) 하나를 고르면 그 요일에 해당하는 날짜별 방문 합계가 나오고,
 * 날짜 하나를 펼치면 그 날의 시간대별 방문(누적 포함)을 볼 수 있다. 원본은
 * 마우스오버로 펼쳤지만 여기서는 클릭 토글로 바꿔 키보드/모바일에서도 쓴다.
 */

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAY_NAMES = WEEKDAYS.map((day) => `${day}요일`);

function hourLabel(hour: number) {
  return `${String(hour).padStart(2, '0')}:00`;
}

export function VisitsPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const weekdayParam = params.get('weekday');
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [pinnedDate, setPinnedDate] = useState<string | null>(null);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDate = hoverDate ?? pinnedDate;

  const days = useQuery({
    queryKey: ['xadm-visit-days', weekdayParam],
    queryFn: () => xadmApi.visitDays(weekdayParam === null ? undefined : Number(weekdayParam)),
  });

  const hours = useQuery({
    queryKey: ['xadm-visit-hours', openDate],
    queryFn: () => xadmApi.visitHours(openDate!),
    enabled: openDate !== null,
  });

  const selectedWeekday = weekdayParam === null ? (days.data?.weekday ?? null) : Number(weekdayParam);
  const openDay = days.data?.days.find((day) => day.date === openDate) ?? null;
  const summary = useMemo(() => {
    const rows = days.data?.days ?? [];
    const latest = rows[0] ?? null;
    const previous = rows[1] ?? null;
    const recentFour = rows.slice(0, 4);
    const trend = [...rows.slice(0, 12)].reverse();
    const delta = latest && previous ? latest.total - previous.total : null;
    return {
      dayCount: rows.length,
      latest,
      previous,
      delta,
      deltaRate: delta === null || !previous?.total ? null : (delta / previous.total) * 100,
      average: recentFour.length === 0
        ? 0
        : Math.round(recentFour.reduce((sum, row) => sum + row.total, 0) / recentFour.length),
      mobileRate: latest?.total ? (latest.mobile / latest.total) * 100 : 0,
      peak: trend.length === 0 ? null : trend.reduce((max, row) => row.total > max.total ? row : max),
      trend,
    };
  }, [days.data]);

  const trendLabels = summary.trend.map((day) => day.date);
  const trendSeries = [
    { id: 'total', label: '전체', points: summary.trend.map((day) => ({ x: day.date, y: day.total })) },
    { id: 'pc', label: 'PC', points: summary.trend.map((day) => ({ x: day.date, y: day.pc })) },
    { id: 'mobile', label: '모바일', points: summary.trend.map((day) => ({ x: day.date, y: day.mobile })) },
  ];

  const selectWeekday = (weekday: number) => {
    setHoverDate(null);
    setPinnedDate(null);
    update({ weekday });
  };

  const cancelHoverClose = () => {
    if (hoverCloseTimer.current !== null) clearTimeout(hoverCloseTimer.current);
    hoverCloseTimer.current = null;
  };

  const previewDate = (date: string) => {
    cancelHoverClose();
    setHoverDate(date);
  };

  const schedulePreviewClose = () => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => setHoverDate(null), 180);
  };

  const togglePinnedDate = (date: string) => {
    cancelHoverClose();
    setPinnedDate((current) => current === date ? null : date);
  };

  useEffect(() => () => cancelHoverClose(), []);

  return (
    <section className="visit-page">
      <header className="page-head visit-page__head">
        <div>
          <h1 className="page-title">방문 통계</h1>
          <p className="page-desc">같은 요일의 방문 변화를 비교하고 시간대별 유입까지 빠르게 탐색합니다.</p>
        </div>
        <span className="visit-page__source"><i aria-hidden="true" /> HOURLY VISIT LOG</span>
      </header>

      <nav className="visit-weekdays" aria-label="조회 요일">
        <span className="visit-weekdays__label">요일 선택</span>
        <div className="visit-weekdays__buttons">
          {WEEKDAYS.map((label, weekday) => (
            <button
              key={weekday}
              type="button"
              className={weekday === selectedWeekday ? 'visit-weekday visit-weekday--active' : 'visit-weekday'}
              aria-pressed={weekday === selectedWeekday}
              title={WEEKDAY_NAMES[weekday]}
              onClick={() => selectWeekday(weekday)}
            >
              {label}
            </button>
          ))}
        </div>
        <p>선택한 요일의 날짜별 합계를 표시합니다.</p>
      </nav>

      {!days.isLoading && !days.isError && summary.dayCount > 0 && (
        <>
        <div className="visit-summary" aria-label="방문 핵심 지표">
          <article className="visit-summary__item visit-summary__item--primary">
            <span>최근 방문</span>
            <strong>{num(summary.latest?.total ?? 0)}</strong>
            <small>{`기준 ${summary.latest?.date}`} · {WEEKDAY_NAMES[selectedWeekday ?? 0]}</small>
          </article>
          <article className={`visit-summary__item visit-summary__item--delta ${summary.delta !== null && summary.delta < 0 ? 'visit-summary__item--down' : ''}`}>
            <span>직전 동일 요일 대비</span>
            <strong>{summary.delta === null ? '—' : `${summary.delta > 0 ? '+' : ''}${num(summary.delta)}`}</strong>
            <small>{summary.deltaRate === null ? '비교 데이터 없음' : `${summary.deltaRate > 0 ? '+' : ''}${summary.deltaRate.toFixed(1)}% 변화`}</small>
          </article>
          <article className="visit-summary__item">
            <span>최근 4회 평균</span>
            <strong>{num(summary.average)}</strong>
            <small>같은 요일 기준</small>
          </article>
          <article className="visit-summary__item visit-summary__item--mobile">
            <span>최근 모바일 비중</span>
            <strong>{summary.mobileRate.toFixed(1)}%</strong>
            <small>모바일 {num(summary.latest?.mobile ?? 0)} · PC {num(summary.latest?.pc ?? 0)}</small>
          </article>
        </div>

        <section className="visit-trend" aria-label="최근 방문 추이">
          <header className="visit-trend__head">
            <div>
              <span>TREND</span>
              <h2>최근 {summary.trend.length}회 방문 추이</h2>
              <p>선택한 {WEEKDAY_NAMES[selectedWeekday ?? 0]}끼리 비교한 결과입니다.</p>
            </div>
            {summary.peak && (
              <div className="visit-trend__peak">
                <span>최근 최고</span>
                <strong>{num(summary.peak.total)}</strong>
                <small>{`기준 ${summary.peak.date}`}</small>
              </div>
            )}
          </header>
          <LineChart series={trendSeries} labels={trendLabels} height={220} />
        </section>
        </>
      )}

      <div className="visit-list-head">
        <div>
          <h2>{selectedWeekday === null ? '방문 기록' : `${WEEKDAY_NAMES[selectedWeekday]} 방문 기록`}</h2>
          <p>날짜 행에 마우스를 올리면 오른쪽에서 시간대별 방문과 누적값을 바로 확인할 수 있습니다.</p>
        </div>
        {!days.isLoading && !days.isError && <span>{num(summary.dayCount)}개 날짜</span>}
      </div>

      {days.isLoading ? (
        <Spinner />
      ) : days.isError ? (
        <ErrorBox error={days.error} />
      ) : days.data!.days.length === 0 ? (
        <EmptyBox />
      ) : (
        <div className="visit-table-wrap">
          <table className="table visit-table">
            <thead>
              <tr>
                <th aria-label="상세 열기" />
                <th>일자</th>
                <th>요일</th>
                <th>PC</th>
                <th>모바일</th>
                <th>디바이스 비중</th>
                <th>TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {days.data!.days.map((day) => {
                const isOpen = openDate === day.date;
                return (
                  <tr
                    key={day.date}
                    className={isOpen ? 'table__row--clickable visit-table__row visit-table__row--open' : 'table__row--clickable visit-table__row'}
                    onMouseEnter={() => previewDate(day.date)}
                    onMouseLeave={schedulePreviewClose}
                    onClick={() => togglePinnedDate(day.date)}
                  >
                    <td className="visit-table__toggle">
                      <button
                        type="button"
                        aria-label={`${day.date} 시간대 상세 ${pinnedDate === day.date ? '닫기' : '고정해서 열기'}`}
                        aria-expanded={isOpen}
                        onClick={(event) => {
                          event.stopPropagation();
                          togglePinnedDate(day.date);
                        }}
                      >
                        <span aria-hidden="true">›</span>
                      </button>
                    </td>
                    <td className="visit-table__date">{day.date}</td>
                    <td><span className={`visit-week-badge visit-week-badge--${day.weekday}`}>{WEEKDAYS[day.weekday]}</span></td>
                    <td className="td-right visit-table__device"><small>PC</small>{num(day.pc)}</td>
                    <td className="td-right visit-table__device"><small>M</small>{num(day.mobile)}</td>
                    <td>
                      <div className="visit-device-mix" aria-label={`PC ${day.pc}명, 모바일 ${day.mobile}명`}>
                        <div>
                          <i style={{ width: `${day.total ? (day.pc / day.total) * 100 : 0}%` }} />
                          <b style={{ width: `${day.total ? (day.mobile / day.total) * 100 : 0}%` }} />
                        </div>
                        <small><span>PC {day.total ? Math.round((day.pc / day.total) * 100) : 0}%</span><span>M {day.total ? Math.round((day.mobile / day.total) * 100) : 0}%</span></small>
                      </div>
                    </td>
                    <td className="td-right visit-table__total">{num(day.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openDate && openDay && (
        <aside
          className="visit-preview"
          aria-label={`${openDay.date} 시간대별 방문`}
          onMouseEnter={cancelHoverClose}
          onMouseLeave={schedulePreviewClose}
        >
          <section className="visit-detail">
            <header className="visit-detail__head">
              <div>
                <span>{WEEKDAY_NAMES[openDay.weekday]}</span>
                <h3>{openDay.date} 시간대별 방문</h3>
              </div>
              <div className="visit-detail__actions">
                <strong>일 방문 {num(openDay.total)}</strong>
                <button
                  type="button"
                  aria-label="시간대별 방문 닫기"
                  onClick={() => {
                    setHoverDate(null);
                    setPinnedDate(null);
                  }}
                >
                  ×
                </button>
              </div>
            </header>
            {hours.isLoading ? (
              <Spinner />
            ) : hours.isError ? (
              <ErrorBox error={hours.error} />
            ) : (hours.data?.hours.length ?? 0) === 0 ? (
              <EmptyBox />
            ) : (
              <div className="visit-detail__table-wrap">
                <table className="table table--nested visit-hour-table">
                  <thead>
                    <tr>
                      <th>시간대</th>
                      <th>PC</th>
                      <th>모바일</th>
                      <th>TOTAL</th>
                      <th>누적</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let cumulative = 0;
                      return (hours.data?.hours ?? []).map((hour) => {
                        cumulative += hour.total;
                        return (
                          <tr key={hour.hour}>
                            <td><span className="visit-hour">{hourLabel(hour.hour)}</span></td>
                            <td className="td-right">{num(hour.pc)}</td>
                            <td className="td-right">{num(hour.mobile)}</td>
                            <td className="td-right visit-hour-table__total">{num(hour.total)}</td>
                            <td className="td-right visit-hour-table__cumulative">{num(cumulative)}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </aside>
      )}
    </section>
  );
}
